import {
  PaperClipOutlined,
  ClockCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { PlayIcon, ReadMessageIcon, UnreadMessageIcon } from '@mm/icons';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import { identifyChannel } from '@mm/state/helpers/live-chat-helpers';
import { LiveChatChannels } from '@mm/state/model/LivechatConversationModel';
import {
  IgMessageStructure,
  LivechatMessageModel,
  MessageKind,
  MessageStructure,
} from '@mm/state/model/LivechatMessageModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectPages } from '@mm/state/selectors/pageSelectors';
import {
  selectCompanyUsers,
  selectCurrentUserId,
} from '@mm/state/selectors/userSelector';
import {
  activeFacebookPageIdSelector,
  formattedBotsSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as AttachmentDownloadIcon } from 'assets/attachment-download.svg';
import { ReactComponent as AgentIcon } from 'assets/avatars/agent.svg';
import { ReactComponent as BotIcon } from 'assets/avatars/bot.svg';
import errorIcon from 'assets/baseline-error_outline.svg';
import classnames from 'classnames';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { colors } from 'packages/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import { ErrorTooltip } from '../../../../../../common/components/ErrorTooltip/ErrorTooltip';
import { Linkify } from '@mm/components';
import { formatMonthDayTime } from '../../../../../../common/helpers/date-formatting';
import {
  CurrentPageNameContext,
  PersonaContext,
} from '../../../HelperContexts';
import { getFormattedDate } from '../../../shared';
import { ChannelIcon, ChannelType } from '../../ChannelIcon';
import { ProfileImage } from '../../ProfileImage/ProfileImage';
import { GalleryDisplay } from '../GalleryDisplay/GalleryDisplay';
import { IGStoryMessage } from '../IGStoryMessage/IGStoryMessage';
import { ImageMessage } from '../ImageMessage/ImageMessage';
import { messageStructureParser } from '../messageStructureParser';
import {
  SearchResultText,
  SearchResultTextProps,
} from '../SearchResultText/SearchResultText';
import { StartedFrom } from '../StartedFrom/StartedFrom';
import styles from './OutgoingMessage.module.css';

type Props = {
  messages?: LivechatMessageModel[];
  message: LivechatMessageModel;
  prevOutgoingMessage?: LivechatMessageModel;
  messageKey: string;
  profilePic: string | undefined;
  isFinalMessage: boolean;
  isFirstMessage: boolean;
  isSearch?: boolean;
  read?: boolean;
} & Omit<SearchResultTextProps, 'messageText'>;

type ImageAttachmentProps = {
  url: string;
};

const renderReadReceipt = (
  channel: ChannelType,
  read?: boolean,
  color?: string,
  className?: string,
  sentFrom?: string,
  mid?: string,
) => {
  if (channel === 'email') {
    if (!mid && !read) {
      return (
        <div className={classnames(styles.sentFrom, styles.orange)}>
          <ClockCircleOutlined />
          Email is in the queue
        </div>
      );
    }

    return (
      <div className={classnames(styles.sentFrom, styles.green)}>
        <SendOutlined />
        {!!sentFrom && <div>Sent from: {sentFrom}</div>}
        {!!read && <ReadMessageIcon color={colors.green5} />}
      </div>
    );
  }
  if (channel === 'instagram' || channel === 'facebook') {
    return (
      <>
        {read ? (
          <ReadMessageIcon
            color={color}
            className={classnames(styles.readReceiptText, className)}
          />
        ) : (
          <UnreadMessageIcon
            color={color}
            className={classnames(styles.readReceiptText, className)}
          />
        )}
      </>
    );
  }
};

// tslint:disable-next-line: cyclomatic-complexity
export const OutgoingMessage = ({
  messages,
  message,
  prevOutgoingMessage,
  messageKey,
  isSearch,
  maybeGetRef,
  getMatchHighlightClassName,
  searchString,
  isFinalMessage,
  isFirstMessage,
  read,
}: Props) => {
  const {
    getDisplayText,
    getTextIsEmojiOnly,
    getQuickReplies,
    getWidgetButtons,
    getDisplayImageSrc,
    getDisplayVideoSrc,
    getDisplayAudioSrc,
    getIsGallery,
    getIsTypingWidget,
    getIsDelayWidget,
    getAttachmentLink,
    getAttachmentLinksWithTypes,
    getOTNMessage,
    getIsLikeHeartSticker,
    isDrip,
    isChatBlaster,
    isAutomation,
    isIgInlineReply,
    isQnA,
    getEmailAttachmentsNames,
  } = messageStructureParser(message);

  const deliveryError =
    message.withDeliveryError &&
    message.responseStructure &&
    !(
      (message.responseStructure === 'null' &&
        message.remoteUserId.startsWith('wc')) ||
      message.structure.isEcho ||
      message.structure.senderAction === 'delay'
    )
      ? message.responseStructure
      : '';

  const smsDeliveryError = useMemo(() => {
    const hasError = !!messages
      ?.filter(m => Number(m.time) > Number(message.time))
      .filter(m => m.kind === MessageKind.fail)
      .some(m => m.mid && message.mid?.includes(m.mid) && !m.withDeliveryError);

    return hasError;
  }, [JSON.stringify(messages), message.mid, message.time]);

  const displayText = getDisplayText();
  const displayImageSrc = getDisplayImageSrc();
  const displayVideoSrc = getDisplayVideoSrc();
  const displayAudioSrc = getDisplayAudioSrc();
  const widgetButtons = getWidgetButtons();
  const quickReplies = getQuickReplies();
  const isGallery = getIsGallery();
  const attachmentLink = getAttachmentLink();
  const otnMessage = getOTNMessage();
  const attachmentLinksWithTypes = getAttachmentLinksWithTypes();
  const initiatedFromDrip = isDrip(isFirstMessage);
  const initiatedFromChatBlaster = isChatBlaster(isFirstMessage);
  const initiatedFromAutomation = isAutomation(isFirstMessage);
  const initiatedFromQnA = isQnA();
  const isLikeHeartSticker = getIsLikeHeartSticker();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isEmojiOnly = useMemo(() => getTextIsEmojiOnly(), [displayText]);
  const isTypingWidget = getIsTypingWidget();
  const isDelayWidget = getIsDelayWidget();
  const isIgInlineReplyMessage = isIgInlineReply();
  const emailAttachmentNames = getEmailAttachmentsNames();

  const failedDeliveryTextByChannel = () => {
    switch (identifyChannel(message.remoteUserId)) {
      case 'facebook': {
        return 'Facebook failed to send this message';
      }
      case 'instagram': {
        return 'Instagram failed to send this message';
      }
      case 'sms': {
        return 'The Carrier failed to send this message';
      }
      case 'webchat': {
        return 'Failed to send message';
      }
      default: {
        return 'Failed to send message';
      }
    }
  };

  const getTypingWidgetDelay = () =>
    message?.structure.seconds
      ? message?.structure.seconds < 3600
        ? message?.structure.seconds < 60
          ? `${Math.ceil(message.structure.seconds)} sec`
          : `${Math.ceil(message.structure.seconds / 60)} min`
        : `${Math.ceil(message?.structure.seconds / 3600)}h`
      : undefined;

  const dialogues = useSelector(selectPages);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const currentUserId = useSelector(selectCurrentUserId);
  const companyUsers = useSelector(selectCompanyUsers);
  const formattedBots = useSelector(formattedBotsSelector);

  const [inLineReplyContent, setInLineReplyContent] = useState<
    string | JSX.Element
  >('...loading');
  const { getLocalIGMessage, getRemoteIGMessage } = useInstagramCRUDActions();
  const fbPageId = useSelector(activeFacebookPageIdSelector);

  const messageStyle =
    deliveryError || smsDeliveryError
      ? styles.messageUndelivered
      : styles.message;
  useEffect(() => {
    if (!isIgInlineReplyMessage) return;

    const getIgReplyContent = (
      parsableStructure: IgMessageStructure | MessageStructure,
    ) => {
      const {
        getDisplayText,
        getAttachmentLinksWithTypes,
        getDisplayImageSrc,
        getDisplayVideoSrc,
        getDisplayAudioSrc,
      } = messageStructureParser({ structure: parsableStructure });

      const displayImageSrc = getDisplayImageSrc();
      const displayVideoSrc = getDisplayVideoSrc();
      const displayAudioSrc = getDisplayAudioSrc();
      const displayText = getDisplayText();
      const attachmentLinksWithTypes = getAttachmentLinksWithTypes();

      if (displayImageSrc) return <ImageMessage url={displayImageSrc} />;
      if (displayVideoSrc) {
        return (
          <div className={styles.bodyVideoContainer}>
            <video controls muted autoPlay loop className={styles.bodyVideo}>
              <source src={displayVideoSrc} />
            </video>
          </div>
        );
      }
      if (displayAudioSrc) {
        return (
          <div className={styles.sharedPostContainer}>
            <div className={styles.audioTitle}>My Voice Message:</div>
            <div className="outgoing-message__body-audio-container">
              <audio controls className="incoming-message__body-audio">
                <source src={displayAudioSrc} />
              </audio>
            </div>
          </div>
        );
      }

      if (attachmentLinksWithTypes) {
        const a = attachmentLinksWithTypes[0];

        if (a) {
          if (a.type === 'image' && a.url) {
            return <ImageMessage url={a.url} />;
          }
          if (a.type === 'video' && a.url) {
            return (
              <div className={styles.bodyVideoContainer}>
                <video
                  controls
                  muted
                  autoPlay
                  loop
                  className={styles.bodyVideo}
                >
                  <source src={a.url} />
                </video>
              </div>
            );
          }
          if (a.type === 'audio' && a.url) {
            return (
              <div className={styles.sharedPostContainer}>
                <div className={styles.audioTitle}>My Voice Message:</div>
                <div className="outgoing-message__body-audio-container">
                  <audio controls className="incoming-message__body-audio">
                    <source src={a.url} />
                  </audio>
                </div>
              </div>
            );
          }
        }
      }

      if (displayText) return displayText;
    };

    const getIgMessage = async () => {
      if (message.structure?.replyTo?.mid && message.remoteUserId) {
        const messageResponse = await getLocalIGMessage(
          message.structure.replyTo?.mid,
          fbPageId,
          message.remoteUserId,
        );

        if (messageResponse?.structures?.length) {
          let counter = 0;

          let content: ReturnType<typeof getIgReplyContent>;
          while (counter < messageResponse.structures.length) {
            content = getIgReplyContent(messageResponse.structures[counter]);
            if (content) {
              break;
            }
            counter = counter + 1;
          }
          if (content) {
            setInLineReplyContent(content);
            return;
          }
        }

        const remoteResponse = await getRemoteIGMessage(
          message.structure.replyTo?.mid,
          fbPageId,
        );

        setInLineReplyContent(remoteResponse?.message || '');
      }
    };
    const preLoadedMessage = messages?.find(
      m => m.structure?.mid === message.structure.replyTo?.mid,
    );

    if (preLoadedMessage) {
      const content = getIgReplyContent(preLoadedMessage?.structure);

      if (content) {
        setInLineReplyContent(content);
        return;
      }
    }
    getIgMessage();
  }, []);

  const currentPersona = useContextSelector(
    PersonaContext,
    v => v.currentPersona,
  );
  const fetchPersona = useContextSelector(PersonaContext, v => v.fetchPersona);
  const { getCurrentPageName } = useContextSelector(
    CurrentPageNameContext,
    v => v,
  );
  const personas = useSelector((state: StoreState) => state.personas.entities);

  const currentPageName = useMemo(
    () => getCurrentPageName(message.currentPageId),
    [getCurrentPageName, message.currentPageId],
  );
  const startedViaLiveChatDialogue =
    message.liveChatResponse && message.currentPageId && message.pageBeginning
      ? dialogues.find(d => d.id === Number(message.currentPageId))
      : undefined;
  const startedViaLiveChatDialogueName = startedViaLiveChatDialogue?.name;

  useEffect(() => {
    if (message.personaId) {
      fetchPersona(Number(message.personaId));
    }
  }, [fetchPersona, message.personaId]);
  const avatar = useMemo(() => {
    if (message.personaId) {
      const persona = personas.find(p => p.id === Number(message.personaId));
      if (persona?.profilePictureUrl) {
        return persona.profilePictureUrl;
      }
    }
    return activeFacebookPage?.squarePicture;
  }, [personas, activeFacebookPage, message.personaId]);

  const personName =
    Number(message.personaId) === currentPersona?.id
      ? currentPersona?.name
      : activeFacebookPage?.name;
  const nameToDisplay = personName ? personName : '';
  const formattedDate = formatMonthDayTime(Number(message.time));
  const channel = identifyChannel(message.remoteUserId);

  const startedNewDialogue =
    message.currentPageId &&
    !message.structure.isEcho &&
    (prevOutgoingMessage?.currentPageId !== message.currentPageId ||
      initiatedFromQnA ||
      initiatedFromChatBlaster ||
      initiatedFromDrip ||
      initiatedFromAutomation ||
      startedViaLiveChatDialogueName);

  const sentFrom = message.accountData;
  const mid = message.mid;

  const [attributionIcon, attribution] = (() => {
    if (message.liveChatResponse) {
      if (message.liveChatUserId === currentUserId.toString()) {
        return [<AgentIcon key="icon" />, 'Sent by me'];
      }

      const sentByUser = companyUsers.find(
        u => u.id.toString() === message.liveChatUserId,
      );
      const userName = sentByUser
        ? `${sentByUser.firstName} ${sentByUser.lastName}`
        : 'agent';

      if (avatar !== activeFacebookPage?.squarePicture) {
        // We're using a persona
        return [<AgentIcon key="icon" />, `Sent by ${userName}`];
      }

      return [<AgentIcon key="icon" />, `Sent by ${userName}`];
    }
    // message.botId === '0' means Imported Contacts with last ten messages for IG or FB
    if (message.structure.isEcho || message.botId === '0') {
      return [
        <ChannelIcon key="icon" channel={channel} />,
        `Sent from ${LiveChatChannels[channel]}`,
      ];
    }

    // Bot response.
    const botName =
      formattedBots.find(b => b.id.toString() === message.botId)?.name || 'bot';
    return [<BotIcon key="icon" />, `Sent by ${botName}`];
  })();

  const igInlineReply = inLineReplyContent && (
    <div className={styles.inlineReplyContainer}>
      <div className={styles.inlineReplyWho}>You replied</div>
      <div className={styles.inlineReplyTextContainer}>
        <div className={styles.inlineReply}>{inLineReplyContent}</div>
      </div>
    </div>
  );

  const getPrimaryContent = () => {
    return (
      <>
        {displayImageSrc && (
          <ImageMessage
            url={displayImageSrc}
            avatar={avatar}
            messageDate={formattedDate}
            personName={nameToDisplay}
          />
        )}
        {isGallery && <GalleryDisplay message={message} />}
        {displayVideoSrc && (
          <div className={styles.bodyVideoContainer}>
            <video controls muted autoPlay loop className={styles.bodyVideo}>
              <source src={displayVideoSrc} />
            </video>
          </div>
        )}

        {displayAudioSrc && (
          <div className={styles.bodyVideoContainer}>
            <audio controls muted className={styles.bodyVideo}>
              <source src={displayAudioSrc} />
            </audio>
          </div>
        )}

        {otnMessage && (
          <>
            <div className={messageStyle}>
              <div className={styles.body}>{otnMessage}</div>
            </div>
            <div className={styles.buttons}>
              <div className={styles.button}>Notify me</div>
            </div>
          </>
        )}

        {(displayText || attachmentLink || isLikeHeartSticker) && (
          <div
            className={classnames(messageStyle, {
              [styles.messageEmojiOnly]: isEmojiOnly || isLikeHeartSticker,
              [styles.messageAttachmentOnly]: attachmentLink,
            })}
          >
            {attachmentLink && (
              <>
                <a
                  className={styles.attachmentLink}
                  href={attachmentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AttachmentDownloadIcon
                    className={styles.attachmentLinkIcon}
                  />
                  {attachmentLink.substring(
                    attachmentLink.lastIndexOf('/') + 1,
                  )}
                </a>
              </>
            )}
            {displayText && (
              <div className={styles.body}>
                {channel === 'email' ? (
                  <>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(displayText),
                      }}
                    />
                    {emailAttachmentNames?.length
                      ? emailAttachmentNames.map(n => (
                          <div key={n} className={styles.emailAttachement}>
                            <PaperClipOutlined
                              className={styles.attachementIcon}
                            />
                            <span>{n}</span>
                          </div>
                        ))
                      : null}
                  </>
                ) : isSearch ? (
                  <SearchResultText
                    message={message}
                    messageText={displayText}
                    maybeGetRef={maybeGetRef}
                    getMatchHighlightClassName={getMatchHighlightClassName}
                    searchString={searchString}
                  />
                ) : (
                  <Linkify css={{ link: styles.link }}>{displayText}</Linkify>
                )}
                {renderReadReceipt(channel, read, undefined, '', sentFrom, mid)}
              </div>
            )}
            {isLikeHeartSticker && (
              <div className={styles.body}>
                <span>{'\u{2764}\u{FE0F}'}</span>️
              </div>
            )}
          </div>
        )}

        {isDelayWidget && (
          <div className={styles.sharedPostContainer}>
            <span className={styles.delayTitle}>
              <i className="fa fa-ellipsis-h" />
              {getTypingWidgetDelay()} delay
            </span>
            ️
          </div>
        )}
        {isTypingWidget && (
          <div className={styles.typingPostContainer}>
            <span className={styles.delayTitle}>
              <i className="fa fa-ellipsis-h" />
              Typing for {message.structure.seconds} sec
            </span>
            ️
          </div>
        )}

        {attachmentLinksWithTypes &&
          attachmentLinksWithTypes.map(a => {
            if (a.type === 'image' && a.url) {
              return (
                <ImageMessage
                  url={a.url}
                  avatar={avatar}
                  messageDate={formattedDate}
                  personName={nameToDisplay}
                />
              );
            }
            if (a.type === 'video' && a.url) {
              return (
                <div className={styles.bodyVideoContainer}>
                  <video
                    controls
                    muted
                    autoPlay
                    loop
                    className={styles.bodyVideo}
                  >
                    <source src={a.url} />
                  </video>
                </div>
              );
            }
            if (a.type === 'file' && a.url) {
              return (
                <div className="outgoing-message outgoing-message--attachment-only">
                  <a
                    className={styles.attachmentLink}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AttachmentDownloadIcon
                      className={styles.attachmentLinkIcon}
                    />
                    {a.url.substring(
                      a.url.lastIndexOf('/') + 1,
                      a.url.lastIndexOf('?'),
                    )}
                  </a>
                </div>
              );
            }
            if (a.type === 'share' && a.url) {
              return (
                <div className={styles.sharedPostContainer}>
                  <span className={styles.sharedPostTitle}>
                    My Shared post:
                  </span>
                  <div
                    className={styles.sharedPostIndicator}
                    onClick={() => window.open(a.url)}
                  >
                    <PlayIcon className="ig-story-indicator-icon" />
                    View Post
                  </div>
                </div>
              );
            }
            if (a.type === 'audio' && a.url) {
              return (
                <div className={styles.sharedPostContainer}>
                  <div className={styles.audioTitle}>My Voice Message:</div>
                  <div className="outgoing-message__body-audio-container">
                    <audio controls className="incoming-message__body-audio">
                      <source src={a.url} />
                    </audio>
                  </div>
                </div>
              );
            }
            if (a.type === 'story_mention' && a.url) {
              return (
                <div className={styles.sharedPostContainer}>
                  <span className={styles.sharedPostTitle}>My Story:</span>
                  <IGStoryMessage
                    css={{
                      container: styles.sharedPostIndicator,
                      content: styles.igStoryText,
                    }}
                    message={message}
                    avatar={avatar}
                    personName={nameToDisplay}
                  />
                </div>
              );
            }

            return null;
          })}
      </>
    );
  };

  return (
    <>
      {initiatedFromQnA && (
        <StartedFrom
          payload={
            message.structure.promoterName ? (
              <>
                <b>Answer triggered by Q&A keyword:</b>{' '}
                {message.structure.promoterName}
              </>
            ) : (
              <b>Answer triggered by Q&A keyword</b>
            )
          }
          ignorePrephrase={true}
        />
      )}
      {initiatedFromDrip && (
        <StartedFrom
          payload={`Drip Campaign ${
            message.campaign?.name ? ` (${message.campaign?.name})` : ''
          }`}
        />
      )}
      {initiatedFromChatBlaster && (
        <StartedFrom
          payload={`Chat Blaster${
            message.campaign?.name ? ` (${message.campaign?.name})` : ''
          }`}
        />
      )}
      {initiatedFromAutomation && (
        <StartedFrom
          payload={`Automation${
            message.campaign?.name ? ` (${message.campaign?.name})` : ''
          }`}
        />
      )}
      <div className={styles.mainContainer}>
        <div>
          {startedViaLiveChatDialogueName && (
            <div className={styles.botReply}>
              <div className={styles.botReplyText}>
                <span>
                  <b>Dialogue sent via Live Chat: </b>
                </span>
                <span>{startedViaLiveChatDialogueName}</span>
              </div>
            </div>
          )}
        </div>

        {isIgInlineReplyMessage && igInlineReply}

        <div
          className={classnames(styles.container, {
            [styles.containerWithOneLineAttribution]:
              startedNewDialogue || message.readAt,
            [styles.containerWithTwoLinesAttribution]:
              startedNewDialogue && message.readAt,
          })}
        >
          <div className={styles.timestamp}>
            {moment(Number(message.time)).format('YYYY-MM-DD HH:mm')}
          </div>

          {startedNewDialogue && (
            <div className={styles.newDialogueMarker}>
              <i className="far fa-comment-dots" />
            </div>
          )}

          {attribution && (
            <div className={styles.attribution}>
              {attribution && <div>{attribution}</div>}
              {!!message.readAt && (
                <div>Read: {getFormattedDate(message.readAt)}</div>
              )}
              {startedNewDialogue && (
                <div>
                  <strong>New Dialogue triggered:</strong>{' '}
                  {`${currentPageName} (ID: ${message.currentPageId})`}
                </div>
              )}
            </div>
          )}

          <div className={styles.messageWrapper} key={messageKey}>
            {getPrimaryContent()}
            {!displayText &&
              !isDelayWidget &&
              renderReadReceipt(
                channel,
                read,
                undefined,
                styles.readReceiptMedia,
                mid,
              )}
            {message.structure.emoji && (
              <div className={styles.messageReaction}>
                {message.structure.emoji}
              </div>
            )}
            {widgetButtons.length > 0 && (
              <div className={styles.buttons}>
                {widgetButtons
                  .filter(btn => btn.title)
                  .map(btn => {
                    return (
                      <div key={btn.title} className={styles.button}>
                        {btn.title}
                      </div>
                    );
                  })}
              </div>
            )}
            {isFinalMessage && quickReplies && (
              <div className={styles.quickReplies}>
                {quickReplies
                  .filter(btn => btn.title)
                  .map(btn => {
                    return (
                      <div key={btn.title} className={styles.quickReply}>
                        {btn.imageUrl && (
                          <img
                            className={styles.quickReplyImage}
                            src={btn.imageUrl}
                          />
                        )}
                        {btn.title}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
          <div className={styles.profileImageContainer}>
            <ProfileImage altText="" profilePictureUrl={avatar} big />
            {attributionIcon && (
              <div className={styles.attributionIcon}>{attributionIcon}</div>
            )}
          </div>
        </div>

        {(deliveryError || smsDeliveryError) && (
          <div className={styles.unsentErrorContainer}>
            <ErrorTooltip
              classPrefix="unsent-message"
              type="error"
              align="start"
              hoverMode={true}
              customIcon={
                <span>
                  <img
                    src={errorIcon}
                    alt=""
                    className={styles.unsentErrorIcon}
                  />
                  {failedDeliveryTextByChannel()}
                </span>
              }
              customMessage={
                smsDeliveryError ? (
                  <div className={styles.unsentErrorTooltip}>
                    No addititional data available.
                  </div>
                ) : (
                  <div className={styles.unsentErrorTooltip}>
                    <div>
                      <b>Error Details:</b>
                    </div>
                    <div>
                      {deliveryError !== 'null'
                        ? deliveryError
                        : 'Something went wrong'}
                    </div>
                  </div>
                )
              }
            />
          </div>
        )}
      </div>
    </>
  );
};
